import { FC, FocusEvent } from 'react';
import { useFormContext } from 'react-hook-form';

import { Grid } from '@mui/material';

import {
  FieldTableRowTitle,
  FieldTableWatchedRowTitle,
} from 'components/_basic/Form/FieldTable2/components/FieldTableRowTitle';
import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { ReadOnlyNumberInput } from 'components/_basic/Form/NumberInput/ReadOnlyNumberInput';

import { concatWithDot } from 'helpers/string';

import { IPersonalExpensesTableRowProps } from './types';

import styles from './PersonalExpensesTableRow.module.scss';

const defaultGetRowName = (name: string, columnName: string, rowIndex?: number) => {
  return concatWithDot(name, rowIndex?.toString() || '', columnName);
};

export const PersonalExpensesTableRow: FC<IPersonalExpensesTableRowProps> = ({
  name,
  rowIndex,
  columnsNames,
  disabled = false,
  rowTitle,
  watchRowTitleName = '',
  titleClass = '',
  firstColumnWidth,
  getRowName = defaultGetRowName,
  readOnly = false,
  allowToEditFieldsIndexes,
  updateRow,
  values,
}) => {
  const { control } = useFormContext();

  const updateRowHandler = ({ target: { value } }: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const valueWithoutCommas = value.replace(/,/g, '');
    const newRow = {
      ...values,
      ['column1']: values['column2'] && valueWithoutCommas ? Number(values['column2']) / Number(valueWithoutCommas) : '',
      ['column0']: valueWithoutCommas,
    };
    updateRow(rowIndex, newRow);
  };

  return (
    <Grid container spacing={1} wrap="nowrap" gap={1} className={styles.container}>
      <Grid item sx={{ width: firstColumnWidth, minWidth: firstColumnWidth }} className={titleClass}>
        {watchRowTitleName && (
          <FieldTableWatchedRowTitle
            watchRowTitleName={watchRowTitleName.replace(
              ':index',
              String(rowIndex === 0 ? 1 : rowIndex === 1 ? 0 : rowIndex),
            )}
          />
        )}
        {rowTitle && !watchRowTitleName && <FieldTableRowTitle title={rowTitle} />}
      </Grid>
      <Grid item xs>
        <Grid container spacing={1} columns={columnsNames.length} wrap="nowrap">
          {columnsNames.map((columnName, index) => {
            const fieldName = getRowName(name, columnName, rowIndex);
            return (
              <Grid key={columnName} item xs={1}>
                {readOnly || !allowToEditFieldsIndexes.includes(index) ? (
                  <ReadOnlyNumberInput
                    id={fieldName}
                    name={fieldName}
                    disabled={disabled}
                    className={styles.rowInputField}
                    defaultNaNValue=""
                  />
                ) : index === 0 ? (
                  <NumberInputControlled
                    control={control}
                    id={fieldName}
                    name={fieldName}
                    disabled={disabled}
                    className={styles.rowInputField}
                    onBlur={updateRowHandler}
                    data-mirror-field={name}
                    allowNegative
                  />
                ) : (
                  <NumberInputControlled
                    control={control}
                    id={fieldName}
                    name={fieldName}
                    disabled={disabled}
                    className={styles.rowInputField}
                    allowNegative
                  />
                )}
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
};
